// #410, Session 78n8. Recording the vendor's own order number AFTER the order
// was placed, which is the only time it can be known. Extracted from
// PurchasingQueuePage.js at Session 79.14c.
//
// INLINE RATHER THAN A SHEET, and that is a deliberate call rather than a
// shortcut. Two reasons, both from reading the file:
//   1. TrackingSheet — the obvious place to bolt this on — is PER ITEM. It
//      writes material_request_items. The vendor's order number belongs to the
//      whole order, on material_requests. Putting a per-order field on a
//      per-line sheet would be wrong the first time a request had two lines.
//   2. Every sheet in this file uses raw `position: fixed`, the six known
//      #148/#250 instances. Adding a seventh to hold ONE text box would be
//      adding a locked-rule-3 violation for a control that fits in a row.
//
// It is also fewer taps, which is the standard this project actually holds
// itself to: read the number off the vendor's email, type it, done.

import React, { useState, useEffect } from 'react';
import { isAwaitingConfirmation } from '../../lib/purchasingQueue';
import ConfirmationBadge from './ConfirmationBadge';

export default function VendorOrderNumberRow({ req, canAction, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(req.vendor_order_ref || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // The panel is reused as the selection moves between requests, so the draft
  // has to follow the record rather than persist across it -- otherwise a
  // half-typed number from one order appears on the next one.
  useEffect(() => {
    setValue(req.vendor_order_ref || '');
    setEditing(false);
    setError('');
  }, [req.id, req.vendor_order_ref]);

  if (req.status !== 'ordered') return null;

  const current = String(req.vendor_order_ref || '').trim();

  async function handleSave() {
    setSaving(true);
    setError('');
    const err = await onSave(req, value);
    setSaving(false);
    if (err) { setError(err); return; }
    setEditing(false);
  }

  return (
    <div style={{
      border: '1px solid #E8E8E8', borderRadius: 8, padding: '10px 12px', marginBottom: 12,
      background: isAwaitingConfirmation(req) ? '#FCFBF7' : 'white',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: current || editing ? 6 : 0 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px' }}>
          Vendor's order number
        </div>
        <ConfirmationBadge req={req} />
      </div>

      {!editing && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ fontSize: 13, fontWeight: current ? 600 : 400, color: current ? '#0F0F0F' : '#7A7A7A', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {/* The empty state says what is missing and WHY it is missing, rather
                than "—". A blank here is not a gap in the record, it is a vendor
                who has not replied yet, and those are different situations. */}
            {current || 'The vendor has not sent a confirmation number yet'}
          </div>
          {canAction && (
            <button onClick={() => setEditing(true)}
              style={{ padding: '5px 10px', background: 'white', color: '#0F0F0F', border: '1px solid #D8D8D8', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
              {current ? 'Edit' : 'Add'}
            </button>
          )}
        </div>
      )}

      {editing && (
        <>
          <input
            type="text" value={value} autoFocus
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') { setEditing(false); setValue(req.vendor_order_ref || ''); } }}
            placeholder="From the vendor's confirmation"
            style={{ width: '100%', height: 36, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 10px', fontSize: 16, boxSizing: 'border-box', marginBottom: 8 }}
          />
          {error && <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 6, padding: '6px 10px', fontSize: 12, marginBottom: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '7px 14px', background: saving ? '#E0E0E0' : '#1D9E75', color: saving ? '#9A9A9A' : 'white', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => { setEditing(false); setValue(req.vendor_order_ref || ''); setError(''); }} disabled={saving}
              style={{ padding: '7px 14px', background: 'white', color: '#5C5C5C', border: '1px solid #D8D8D8', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}
